<template>
  <div class="container-fluid">
    <div class="card mb-25 border-0 rounded-0 bg-white shadow-sm">
      <!-- Header bleu -->
      <div class="card-header d-flex justify-content-between align-items-center py-4 px-4"
        style="background-color: #0a59a4;">
        <div>
          <h3 class="text-white fw-bold mb-1">
            Détail de la zone tarifaire – {{ tariffZone.name || 'Chargement...' }}
          </h3>
          <small class="text-white opacity-75">
            Code : {{ tariffZone.code }} • Créée le {{ formatDate(tariffZone.createdAt) }}
          </small>
        </div>
        <div>
          <!-- Badges statut -->
          <span class="badge fs-5 px-4 py-2 me-2" :class="tariffZone.isActive ? 'bg-success' : 'bg-secondary'">
            {{ tariffZone.isActive ? 'Active' : 'Inactive' }}
          </span>
          <span v-if="tariffZone.isDefault" class="badge fs-5 px-4 py-2 bg-warning text-dark">
            Zone par défaut
          </span>
        </div>
      </div>

      <div class="card-body p-15 p-sm-20 p-md-30 letter-spacing">
        <!-- ====================== INFORMATIONS GÉNÉRALES ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Informations générales</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-4">
            <label class="text-black fw-semibold">Code</label>
            <p class="mb-0 fw-bold">{{ tariffZone.code || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Nom</label>
            <p class="mb-0 fw-bold">{{ tariffZone.name || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Statut</label>
            <p class="mb-0">
              <span class="badge" :class="tariffZone.isActive ? 'bg-success' : 'bg-secondary'">
                {{ tariffZone.isActive ? 'Active' : 'Inactive' }}
              </span>
            </p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Zone par défaut</label>
            <p class="mb-0">
              <i :class="tariffZone.isDefault ? 'fas fa-check text-success' : 'fas fa-times text-muted'"></i>
              {{ tariffZone.isDefault ? 'Oui' : 'Non' }}
            </p>
          </div>
          <div class="col-md-8">
            <label class="text-black fw-semibold">Description</label>
            <p class="mb-0">{{ tariffZone.description || '-' }}</p>
          </div>
        </div>

        <!-- ====================== PORTÉE GÉOGRAPHIQUE ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Portée géographique</h5>
        </div>
        <div class="row g-4 mb-5">
          <!-- NOUVEAU : Continents -->
          <div class="col-md-12">
            <label class="text-black fw-semibold">Continent(s) d'origine du chargement</label>
            <div v-if="tariffZone.continent && tariffZone.continent.length > 0" class="d-flex flex-wrap gap-2 mt-2">
              <span v-for="cont in tariffZone.continent" :key="cont" class="badge bg-info fs-6">
                {{ getContinentLabel(cont) }}
              </span>
            </div>
            <p v-else class="mb-0 text-muted">Aucun continent spécifié</p>
            <small class="text-muted d-block mt-2">
              Les continents pour lesquels cette zone tarifaire s'applique
            </small>
          </div>

          <!-- <div class="col-md-4">
            <label class="text-black fw-semibold">Zone géographique</label>
            <p class="mb-0">{{ tariffZone.zone?.name || 'Toutes les zones' }}</p>
            <small class="text-muted" v-if="!tariffZone.zone">Aucune restriction (toutes zones)</small>
          </div> -->
          <div class="col-md-4">
            <label class="text-black fw-semibold">Pays</label>
            <p class="mb-0">
              {{ tariffZone.pays ? `${tariffZone.pays.iso2_code} - ${tariffZone.pays.name_fr}` : 'Tous les pays' }}
            </p>
            <small class="text-muted" v-if="!tariffZone.pays">Aucune restriction (tous pays)</small>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Port</label>
            <p class="mb-0">
              {{ tariffZone.port ? `${tariffZone.port.nomPort} (${tariffZone.port.locode})` : 'Tous les ports' }}
            </p>
            <small class="text-muted" v-if="!tariffZone.port">Aucune restriction (tous ports)</small>
          </div>
        </div>

        <!-- ====================== PÉRIODE DE VALIDITÉ ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Période de validité</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-6">
            <label class="text-black fw-semibold">Date de début</label>
            <p class="mb-0">{{ tariffZone.effectiveFrom ? formatDate(tariffZone.effectiveFrom) : 'Immédiate' }}</p>
          </div>
          <div class="col-md-6">
            <label class="text-black fw-semibold">Date de fin</label>
            <p class="mb-0">
              {{ tariffZone.effectiveTo ? formatDate(tariffZone.effectiveTo) : 'Aucune (illimitée)' }}
            </p>
          </div>
        </div>

        <!-- ====================== MÉTADONNÉES ====================== -->
        <div class="mb-4 mt-5">
          <h5 class="text-primary fw-bold border-bottom pb-2">Métadonnées</h5>
        </div>
        <div class="row g-4">
          <div class="col-md-4">
            <label class="text-black fw-semibold">Créée le</label>
            <p class="mb-0">{{ formatDateTime(tariffZone.createdAt) }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Modifiée le</label>
            <p class="mb-0">{{ tariffZone.updatedAt ? formatDateTime(tariffZone.updatedAt) : '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">ID interne</label>
            <p class="mb-0 text-muted small">{{ tariffZone.id }}</p>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="d-flex justify-content-between mt-5 pt-4 border-top">
          <router-link to="/listeTariffZone" class="btn btn-secondary px-4">
            <i class="fas fa-arrow-left me-2"></i>
            Retour à la liste
          </router-link>

          <div>
            <router-link :to="{ name: 'EditTariffZonePage', params: { id: tariffZone.id } }"
              class="btn btn-warning text-white px-4 me-2">
              <i class="flaticon-edit me-2"></i>
              Modifier
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import { error } from "@/utils/utils";

export default defineComponent({
  name: "ViewTariffZone",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const zoneId = route.params.id as string;

    const tariffZone = ref<any>({});
    const loading = ref(true);

    const formatDate = (date: string | null) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fr-FR');
    };

    const formatDateTime = (date: string | null) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('fr-FR');
    };

    const getContinentLabel = (value: string) => {
      const map: Record<string, string> = {
        AFRIQUE: 'Afrique',
        EUROPE: 'Europe',
        ASIE: 'Asie',
        AMERIQUE: 'Amérique',
        OCEANIE: 'Océanie',
      };
      return map[value] || value;
    };

    const loadTariffZone = async () => {
      try {
        loading.value = true;
        const response = await ApiService.get(`/tariff-zone/${zoneId}`);
        tariffZone.value = response.data.data;

        // Formatage des relations pour affichage clair
        if (tariffZone.value.zone) {
          tariffZone.value.zone = { id: tariffZone.value.zone.id, name: tariffZone.value.zone.name };
        }
        if (tariffZone.value.pays) {
          tariffZone.value.pays = {
            id: tariffZone.value.pays.id,
            iso2_code: tariffZone.value.pays.iso2_code,
            name_fr: tariffZone.value.pays.name_fr
          };
        }
        if (tariffZone.value.port) {
          tariffZone.value.port = {
            id: tariffZone.value.port.id,
            nomPort: tariffZone.value.port.nomPort,
            locode: tariffZone.value.port.locode
          };
        }
      } catch (err: any) {
        error(err.response?.data?.message || "Impossible de charger la zone tarifaire");
        router.push("/listeTariffZone");
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadTariffZone();
    });

    return {
      tariffZone,
      loading,
      formatDate,
      formatDateTime,
      getContinentLabel,
    };
  },
});
</script>

<style scoped>

.text-primary { color: #0a59a4 !important; }
.section-title h5 {
  position: relative;
  padding-left: 15px;
}
.section-title h5::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background-color: #0a59a4;
  border-radius: 2px;
}

.bg-blue-header {
  background-color: #0a59a4 !important;
}

.card {
  border-radius: 12px;
  overflow: hidden;
}

label {
  font-size: 0.9rem;
  color: #495057;
}
</style>